import type { UserRole } from '../types/api'
import { apiClient } from './client'

export interface InvitationRow {
  id: number
  email: string | null
  username: string | null
  githubUsername: string | null
  role: UserRole
  status: 'invited' | 'consumed' | 'revoked'
  expiresAt: string | null
  createdAt: string
  userId: number | null
  hasPassword: boolean
  deliveryStatus: 'not_sent' | 'sent' | 'failed'
  legacy: boolean
}
export interface InvitationResult {
  invitation: InvitationRow
  emailSent: boolean
  invitationUrl: string
}
const path = (id?: number, action = '', orgId?: number) =>
  `/admin/people/invitations${id === undefined ? '' : `/${id}`}${action}${orgId === undefined ? '' : `?orgId=${orgId}`}`

export const listInvitations = (orgId?: number) =>
  apiClient.get<InvitationRow[]>(path(undefined, '', orgId))
export const invitePerson = (input: {
  email: string
  username: string
  role: UserRole
  password?: string
  orgId?: number
}) => {
  const { orgId, ...body } = input
  return apiClient.post<InvitationResult>(path(undefined, '', orgId), body)
}
export const resendInvitation = (id: number, orgId?: number) =>
  apiClient.post<InvitationResult>(path(id, '/resend', orgId))
export const getInvitationLink = (id: number, orgId?: number) =>
  apiClient.get<{ invitationUrl: string }>(path(id, '/link', orgId))
export const revokeInvitation = (id: number, orgId?: number) =>
  apiClient.post(path(id, '/revoke', orgId))
export const updateInvitationRole = (id: number, role: UserRole, orgId?: number) =>
  apiClient.patch<InvitationRow>(path(id, '', orgId), { role })

export interface PublicInvitation {
  email: string
  organizationName: string
  orgSlug: string
  hasPassword: boolean
  expiresAt: string
  providers: string[]
}
export class InvitationError extends Error {
  constructor(
    public code: string,
    message: string
  ) {
    super(message)
  }
}
export async function invitationRequest<T>(
  orgSlug: string,
  token: string,
  password?: string
): Promise<T> {
  const response = await fetch(
    `/api/auth/invitations/${encodeURIComponent(orgSlug)}/${encodeURIComponent(token)}${password === undefined ? '' : '/password-login'}`,
    {
      method: password === undefined ? 'GET' : 'POST',
      credentials: 'include',
      referrerPolicy: 'no-referrer',
      ...(password === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) }),
    }
  )
  const data = await response.json()
  if (!response.ok) {
    const error = typeof data.error === 'object' && data.error !== null ? data.error : data
    throw new InvitationError(
      error.code ?? 'invalid_invitation',
      error.message ?? 'Unable to open invitation'
    )
  }
  return data as T
}
